<template>
  <div
    v-if="invoice"
    class="invoice-header d-flex flex-col md:flex-row items-end md:items-center justify-content-between align-items-center mb-3 gap-3"
  >
    <div class="block">
      <div class="flex items-center flex-wrap">
        <slot name="title">
          <h3 class="page-header-2" data-cy="invoice-title">
            Invoice #{{ invoice.invoice_id }}
          </h3>
        </slot>
        <div
          v-if="invoice.qbo_sync_status === 'succeeded'"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <QBSyncBadge :url="invoice.qbo_url" />
        </div>
        <div
          v-if="invoice.exclude_from_batching"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <Badge
            icon="fa-triangle-exclamation"
            class="ml-0"
            color="yellow"
            text="Do Not Batch"
          />
        </div>
        <div class="divider"></div>
        <Badge class="ml-0" v-bind="badgeParams" />
        <div v-if="spbBadgeVisible" class="d-flex align-items-center">
          <div class="divider"></div>
          <SpbStatusBadge :invoice="invoice" />
        </div>
        <div
          v-if="invoice.invoice_last_emailed_at"
          class="d-flex align-items-center"
        >
          <div class="divider"></div>
          <Badge class="ml-0" v-bind="badgeSentParams" />
        </div>
      </div>
    </div>
    <div class="flex actions-wrapper">
      <InvoiceActionsContextMenu
        class="ml-auto"
        :invoice="invoice"
        :tippy-options="tippyOptions"
        has-button-menu
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";
import { invoiceBadgeParams, sentBadgeParams } from "@/lib/badgeHelpers.js";
import { EventBus } from "@/lib/event_bus";

import Badge from "@/components/Badge.vue";
import InvoiceActionsContextMenu from "@/components/Invoice/InvoiceActionsContextMenu.vue";
import SpbStatusBadge from "@/Invoice/SpbStatusBadge.vue";
import QBSyncBadge from "@/Quickbooks/QBSyncBadge.vue";
import type { Invoice } from "@/typings/invoice";
import type { TippyOptions } from "@/typings/ui";

const props = defineProps<{
  invoice: Invoice;
}>();

const store = useStore();

const tippyOptions: TippyOptions = {
  distance: 10,
  placement: "bottom",
  trigger: "click",
  hideOnClick: true,
  theme: "light"
};

const badgeParams = computed(() => invoiceBadgeParams(props.invoice));
const badgeSentParams = computed(() =>
  sentBadgeParams(props.invoice.invoice_last_emailed_at)
);

const spbBadgeVisible = computed(
  () => props.invoice?.progress_billing?.is_progress_billing === true
);

function reloadCustomerAndInvoice() {
  store.dispatch("fetchInvoice", props.invoice.invoice_id);
  const customerId = props.invoice?.customer?.customer_id;
  if (customerId) {
    store.dispatch("fetchUser", { id: customerId });
  }
}

const busEvents = [
  "payment-method-updated",
  "payment-method-removed",
  "invoice-resubmitted",
  "invoice-cancelled",
  "invoice-unapproved",
  "invoice-voided",
  "invoice-date-updated",
  "invoice-summary-updated"
];

onMounted(() => {
  busEvents.forEach((e) => EventBus.$on(e, reloadCustomerAndInvoice));
});

onBeforeUnmount(() => {
  busEvents.forEach((e) => EventBus.$off(e, reloadCustomerAndInvoice));
});
</script>

<style lang="scss" scoped>
.invoice-header {
  white-space: nowrap;
}
.invoice-badge {
  margin-right: auto;
}
.divider {
  width: 1px;
  margin: 0 12px;
  height: 30px;
  background: var(--sera-color-neutral-gray-med-gray-500);
}
</style>
